import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RsaEncryptionService {
  private cryptoKeyCache$: Observable<CryptoKey> | null = null;

  constructor(private http: HttpClient) {}

  private pemToCryptoKey(pem: string): Promise<CryptoKey> {
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    
    const pemContents = pem
      .replace(pemHeader, '')
      .replace(pemFooter, '')
      .replace(/\s/g, '');

    const binaryString = window.atob(pemContents);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return window.crypto.subtle.importKey(
      'spki',
      bytes.buffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      true,
      ['encrypt']
    ).then(async key => {
      const algo = key.algorithm as RsaHashedKeyAlgorithm;
      console.log('✅ [RSA] Clave importada:', algo.name, '+', (algo.hash as any).name, '(' + algo.modulusLength + ' bits)');
      return key;
    }).catch(error => {
      console.error('❌ [RSA] Error al importar clave');
      throw error;
    });
  }

  getPublicKey(apiUrl: string): Observable<CryptoKey> {
    if (this.cryptoKeyCache$) {
      return this.cryptoKeyCache$;
    }

    this.cryptoKeyCache$ = this.http.get<any>(`${apiUrl}/public-key`).pipe(
      map(response => {
        if (!response.publicKey) {
          throw new Error('Respuesta inválida: falta publicKey');
        }
        
        let publicKey = response.publicKey;
        
        if (publicKey.includes('\\n')) {
          publicKey = publicKey.replace(/\\n/g, '\n');
        }
        
        return publicKey;
      }),
      switchMap(pemKey => from(this.pemToCryptoKey(pemKey))),
      shareReplay(1)
    );

    return this.cryptoKeyCache$;
  }

  encrypt(data: any, apiUrl: string): Observable<string> {
    console.log('🔐 [RSA] Iniciando cifrado');

    return this.getPublicKey(apiUrl).pipe(
      switchMap(cryptoKey => {
        const jsonData = JSON.stringify(data);
        console.log('📏 [RSA] Tamaño de datos:', jsonData.length, 'bytes');
        
        const maxSize = 190;
        if (jsonData.length > maxSize) {
          console.error('❌ [RSA] Datos demasiado grandes:', jsonData.length, 'bytes (máx:', maxSize, 'bytes)');
          throw new Error(`Datos demasiado grandes para RSA-OAEP (${jsonData.length} bytes). Máximo: ${maxSize} bytes`);
        }

        const algo = cryptoKey.algorithm as RsaHashedKeyAlgorithm;
        console.log('🔑 [RSA] Algoritmo:', algo.name, '+', (algo.hash as any).name, '(' + algo.modulusLength + ' bits)');
        
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(jsonData);

        return from(
          window.crypto.subtle.encrypt(
            { name: 'RSA-OAEP' },
            cryptoKey,
            dataBuffer
          ).then(result => {
            console.log('✅ [RSA] Cifrado completado:', result.byteLength, 'bytes');
            return result;
          }).catch(error => {
            console.error('❌ [RSA] Error al cifrar');
            throw error;
          })
        );
      }),
      map(encryptedBuffer => {
        const encryptedArray = new Uint8Array(encryptedBuffer);
        let binary = '';
        for (let i = 0; i < encryptedArray.byteLength; i++) {
          binary += String.fromCharCode(encryptedArray[i]);
        }
        const base64 = window.btoa(binary);

        console.log('✅ [RSA] Payload cifrado generado:', base64.length, 'caracteres');
        
        return base64;
      })
    );
  }

  clearCache(): void {
    this.cryptoKeyCache$ = null;
  }
}
